import { allPass, anyPass, find, forEach, isNil, map, pathOr, propEq, replace, slice } from 'ramda';
import { MediaType } from '../../../constants/MediaType';
import { TMMGRSettings } from '../../../utils/hoc/withMMGRSettings/withMMGRSettings.types';
import { MediaManagerSDKFile, MediaManagerSDKFileOutputAudio } from '../../../utils/media-manager-sdk';
import { isPictureFile, isSWFFile, isSiteFilesItem, isVideoFile } from '../../../utils/media-manager-sdk/items';

const filterDomain = replace(/(http:\/\/[^\/]+\/)/i, '');

// TODO fix public media inconsistent guid format, watch adapters/public/adapter
const filterFileUrl = replace(/.*\//, '');

type TMappedItem = Partial<
  MediaManagerSDKFile & {
    fileName: string;
    uri: string;
    title: string;
    fullTitle: string;
    previewFileName: string;
    fileBaseUrl: string;
  }
>;

const attributesMapG6ToG5 = {
  source: 'sourceName',
};

const shouldSubmitWidthAndHeight = anyPass([isPictureFile, isSWFFile, isVideoFile]);
const shouldNotFilterFileName = allPass([isSiteFilesItem, isVideoFile]);

export const prepareItemsBeforeSubmit = (settings: TMMGRSettings, items: MediaManagerSDKFile[]): object =>
  map((item) => {
    const {
      id,
      mediaType,
      name = '', // in case we don't get name from ADI
      width,
      height,
      fileInput,
      fileOutput,
      embedHtml,
      externalSources,
      sampleUrl,
      fileUrl = '',
      fileSize,
      transcodingStatus,
    } = item;

    const mapped: TMappedItem = {
      id,
      mediaType,
      fileSize,
      // This always returned 'file' for videos from Site Files, added shouldNotFilterFileName
      fileName: shouldNotFilterFileName(item) ? id : filterFileUrl(fileUrl),
      uri: filterFileUrl(fileUrl), // for TPA-SDK
      fileUrl,
      title: slice(0, 90, name || ''),
      fullTitle: name,
      fileInput,
      fileOutput,
      transcodingStatus,
    };

    if (shouldSubmitWidthAndHeight(item)) {
      mapped.width = width;
      mapped.height = height;
    }

    if (item.mediaType === MediaType.SecureMusic) {
      const preview = find<MediaManagerSDKFileOutputAudio>(
        propEq('tag', 'preview'),
        pathOr<MediaManagerSDKFileOutputAudio[], MediaManagerSDKFileOutputAudio[]>([], ['fileOutput', 'audio'], item),
      );
      mapped.previewFileName = preview && preview.url.replace(/.*\//, '');
      mapped.sampleUrl = sampleUrl;
    }

    if (item.mediaType === MediaType.Video) {
      mapped.embedHtml = embedHtml;
      mapped.externalSources = externalSources;
      mapped.fileBaseUrl = fileUrl.split('/').slice(0, 2).join('/');
    }

    forEach((attrName: string) => {
      if (!isNil(mapped[attrName])) {
        return;
      }

      mapped[attributesMapG6ToG5[attrName] || attrName] = /Url/i.test(attrName)
        ? filterDomain(item[attrName])
        : item[attrName];
    }, settings.additionalItemAttributes);

    return mapped;
  }, items);
